/**
 * In-memory vault session state (Task #84, expanded in Task #100).
 *
 * Holds the unlocked MEK + identity keypair only in JS memory. We deliberately
 * do NOT persist them to localStorage, sessionStorage, IndexedDB, cookies, a
 * Service Worker, or any other on-disk / cross-page-load surface — unlock
 * happens fresh per session. Tab close, refresh, and account-switch all wipe
 * the keys.
 *
 * Task #100 adds:
 *   - A real idle timer that gets reset by user activity via `bumpVaultActivity`
 *     (previously this function existed but was never called, making the timer
 *     a hard cap rather than an idle timer).
 *   - A user-configurable idle window (1, 5, 10, or 15 minutes — capped at 15).
 *   - A two-phase timer that fires an "idle-warning" event 30s before the lock
 *     so the UI can show a soft "Still here?" prompt instead of yanking the
 *     vault out from under the user with no warning.
 */

import { useSyncExternalStore } from "react";
import type { X25519Keypair } from "./crypto";

export interface VaultSession {
  mek: Uint8Array;
  identity: X25519Keypair;
  unlockedAt: number;
}

const DEFAULT_IDLE_MINUTES = 15;
const MAX_IDLE_MINUTES = 15;
const MIN_IDLE_MINUTES = 1;
export const IDLE_WARNING_MS = 30 * 1000; // soft-prompt window before lock

let session: VaultSession | null = null;
let idleLockMs = DEFAULT_IDLE_MINUTES * 60 * 1000;
let warningTimer: ReturnType<typeof setTimeout> | null = null;
let lockTimer: ReturnType<typeof setTimeout> | null = null;
const sessionListeners = new Set<() => void>();

type VaultEvent = "idle-warning" | "idle-dismiss";
const eventListeners: Map<VaultEvent, Set<() => void>> = new Map();

function emit(event: VaultEvent) {
  const set = eventListeners.get(event);
  if (set) set.forEach((l) => l());
}

export function subscribeVaultEvent(event: VaultEvent, cb: () => void): () => void {
  let set = eventListeners.get(event);
  if (!set) {
    set = new Set();
    eventListeners.set(event, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
  };
}

// Coalesce + defer subscriber notifications (Task #156).
//
// `unlockVault()` is called from inside the password / recovery-phrase /
// biometric click handlers — and at that exact instant VaultGate is about
// to swap its rendered tree from <VaultUnlockModal> to the children. If we
// notify every `useSyncExternalStore` subscriber SYNCHRONOUSLY in that same
// stack frame, React commits a brand-new tree while the previous tree's
// commit phase is still in flight, which surfaces as a "Try again" error
// card on every single login.
//
// The fix is one microtask of slack: queue the notify on the microtask
// queue (runs after the current React commit, before the next paint) and
// coalesce overlapping requests so a flurry of `unlockVault`/`lockVault`
// calls inside the same tick still produces exactly one subscriber wakeup.
let notifyPending = false;
function notify() {
  if (notifyPending) return;
  notifyPending = true;
  queueMicrotask(() => {
    notifyPending = false;
    sessionListeners.forEach((l) => l());
  });
}

function clearIdleTimers() {
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
  if (lockTimer) {
    clearTimeout(lockTimer);
    lockTimer = null;
  }
}

function scheduleIdleLock() {
  const warningWasPending = warningTimer !== null;
  const lockWasPending = lockTimer !== null;
  clearIdleTimers();
  // If we were inside the 30-second warning window when the user did
  // something, dismiss the open soft-prompt now that activity reset the timer.
  if (warningWasPending || lockWasPending) {
    emit("idle-dismiss");
  }
  const warningDelay = Math.max(0, idleLockMs - IDLE_WARNING_MS);
  warningTimer = setTimeout(() => {
    warningTimer = null;
    emit("idle-warning");
    lockTimer = setTimeout(() => {
      lockTimer = null;
      lockVault();
    }, IDLE_WARNING_MS);
  }, warningDelay);
}

export function setIdleLockMinutes(minutes: number) {
  const clamped = Math.min(
    MAX_IDLE_MINUTES,
    Math.max(MIN_IDLE_MINUTES, Math.round(minutes)),
  );
  idleLockMs = clamped * 60 * 1000;
  if (session) scheduleIdleLock();
}

export function getIdleLockMinutes(): number {
  return Math.round(idleLockMs / 60000);
}

export function unlockVault(mek: Uint8Array, identity: X25519Keypair) {
  // Wipe any prior session (e.g. account switch) before installing the new one
  // so MEK/private-key bytes from a different account never linger in memory.
  if (session) {
    session.mek.fill(0);
    session.identity.privateKey.fill(0);
  }
  session = { mek, identity, unlockedAt: Date.now() };
  scheduleIdleLock();
  notify();
}

export function lockVault() {
  if (session) {
    // Best-effort wipe — JS can't guarantee secure erase but at least drop
    // the strong references so the GC can reclaim them.
    session.mek.fill(0);
    session.identity.privateKey.fill(0);
  }
  session = null;
  clearIdleTimers();
  // Dismiss any open idle prompt — the vault is gone, the prompt is moot.
  emit("idle-dismiss");
  notify();
}

export function getVaultSession(): VaultSession | null {
  // Reference-stability contract (Task #156): this function MUST return the
  // SAME object identity between calls whenever the underlying state hasn't
  // changed. `useSyncExternalStore` calls this as both `getSnapshot` and
  // `getServerSnapshot` and uses `Object.is` to decide whether to re-render
  // — returning a fresh `{ ... }` here would cause an infinite render loop.
  // The module-level `session` variable is only reassigned inside
  // `unlockVault` / `lockVault`, so direct return is correct. Do not wrap.
  return session;
}

export function isVaultUnlocked(): boolean {
  return session !== null;
}

export function getMek(): Uint8Array | null {
  return session?.mek ?? null;
}

export function bumpVaultActivity() {
  if (session) scheduleIdleLock();
}

function subscribe(cb: () => void) {
  sessionListeners.add(cb);
  return () => {
    sessionListeners.delete(cb);
  };
}

export function useVaultSession(): VaultSession | null {
  return useSyncExternalStore(subscribe, getVaultSession, getVaultSession);
}
